import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import { useSkinSession } from '../context/SkinSessionContext';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { createNewSessionWithBackend, pathWithSessionId } from '../utils/sessionManager';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { applyPageSetupResult, storePageSetupData, getPageSetupDataKey } from '../components/PageBackgroundSync';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { getApiBaseUrl } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
/* Landing styles last so they win over shared page/button CSS (same specificity / cascade) */
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './LandingPage.css';

/** Decode HTML entities so escaped API content (e.g. &lt;h1&gt;) renders as real HTML */
function decodeHtmlEntities(html) {
  if (!html || typeof html !== 'string') return html;
  const entities = { '&lt;': '<', '&gt;': '>', '&amp;': '&', '&quot;': '"', '&#39;': "'", '&apos;': "'" };
  return html.replace(/&(?:lt|gt|amp|quot|#39|apos);/g, (m) => entities[m] || m);
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { token: tokenFromParams } = useParams();
  const { token: sessionToken, setToken } = useSkinSession();
  const token = tokenFromParams || sessionToken;
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ctaPosition, setCtaPosition] = useState('top'); // 'top' (default) or 'bottom'
  const [ctaText, setCtaText] = useState(''); // from page-setup external_fields.cta.text
  const [ctaStyle, setCtaStyle] = useState(''); // from page-setup external_fields.cta.style (e.g. style_4)
  const [orgLogoUrl, setOrgLogoUrl] = useState(''); // from page-setup external_fields.org_logo
  const [buttonConfig, setButtonConfig] = useState(null); // button styling from landing-page data
  // Avoid showing CSS fallback gradient until API has set --page-bg-* (prevents flash)
  const [backgroundReady, setBackgroundReady] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  useEffect(() => {
    if (tokenFromParams) setToken(tokenFromParams);
  }, [tokenFromParams, setToken]);

  // When no token, show fallback background immediately (no fetch).
  useEffect(() => {
    if (!token) setBackgroundReady(true);
  }, [token]);

  // Restore CTA text/style and org logo from stored page-setup on mount (e.g. after refresh).
  useEffect(() => {
    if (!token) return;
    const key = getPageSetupDataKey(token);
    if (!key) return;
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        const ext = parsed?.external_fields;
        const cta = ext?.cta;
        if (cta?.text) setCtaText(cta.text);
        if (cta?.style) setCtaStyle(String(cta.style));
        if (ext?.org_logo && typeof ext.org_logo === 'string') setOrgLogoUrl(ext.org_logo);
      }
    } catch (_) {}
  }, [token]);

  const fetchedTokenRef = useRef('');
  useEffect(() => {
    if (!token) return;
    if (fetchedTokenRef.current === token) return;
    fetchedTokenRef.current = token;

    const landingUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/landing-page`;
    const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;

    const fetchLandingData = async () => {
      setIsLoading(true);
      try {
        const [landingRes, pageSetupRes] = await Promise.all([
          fetchWithTimeout(landingUrl, { timeoutMs: 20000 }),
          fetchWithTimeout(pageSetupUrl, { timeoutMs: 20000 }),
        ]);

        if (pageSetupRes.ok) {
          const pageSetupResult = await pageSetupRes.json();
          applyPageSetupResult(pageSetupResult);
          storePageSetupData(token, pageSetupResult);
          const ext = pageSetupResult?.external_fields;
          const cta = ext?.cta;
          if (cta?.text) setCtaText(cta.text);
          if (cta?.style) setCtaStyle(String(cta.style));
          if (ext?.org_logo && typeof ext.org_logo === 'string') setOrgLogoUrl(ext.org_logo);
        }

        if (landingRes.ok) {
          const result = await landingRes.json();
          const data = result.data || result;
          const remoteThumbnail = data?.thumbnail ||
            (Array.isArray(result) && result[0]?.thumbnail);
          if (remoteThumbnail) setImageUrl(remoteThumbnail);

          const remoteDescription = data?.description ||
            (Array.isArray(result) && result[0]?.description);
          if (remoteDescription) setDescription(remoteDescription);

          const position = data?.cta_position ||
            (Array.isArray(result) && result[0]?.cta_position);
          if (position === 'top' || position === 'bottom') setCtaPosition(position);

          const remoteCtaText = data?.cta_text ?? result?.cta_text ?? (Array.isArray(result) && result[0]?.cta_text);
          if (remoteCtaText != null && String(remoteCtaText).trim()) setCtaText(String(remoteCtaText).trim());

          // Extract button configuration (colors, gradient type)
          const btnConfig = data?.button || result?.button;
          if (btnConfig && typeof btnConfig === 'object') setButtonConfig(btnConfig);
        }
      } catch (_) {
        // show fallback on error
      } finally {
        setIsLoading(false);
        setBackgroundReady(true);
      }
    };

    fetchLandingData();
  }, [token]);

  const handleStartAnalysis = async () => {
    setIsButtonLoading(true);
    const deviceInfo = captureDeviceInfo();
    const API_BASE_URL = getApiBaseUrl() || '';
    let sessionId = null;
    try {
      sessionId = await createNewSessionWithBackend(API_BASE_URL, {
        app_session_token: token || undefined,
        device_info: deviceInfo,
      });
      const isBackendSession = sessionId && (/^[A-Z0-9_]+_ID_\d+$/i.test(sessionId) || /^\d+$/.test(sessionId));
      if (!isBackendSession) sessionId = null;
      const path = pathWithSessionId('/user-info', sessionId);
      navigate(path, {
        state: {
          ...(token ? { token } : {}),
          deviceInfo,
          ...(sessionId ? { sessionId } : {}),
        },
      });
    } catch {
      setIsButtonLoading(false);
    }
  };

  const ctaButtonLabel = ctaText?.trim() || 'Start Your Analysis';
  const CTA_STYLE_CLASSES = {
    style_1: 'rounded-lg',
    style_2: 'rounded-xl',
    style_3: 'rounded-2xl',
    style_4: 'rounded-full',
  };
  const ctaStyleClass = CTA_STYLE_CLASSES[ctaStyle?.trim()] || '';


  // Build button inline styles from button configuration
  const getButtonStyles = () => {
    if (!buttonConfig) return {};
    const styles = {};
    
    // Apply gradient if background_type is 'gradient'
    if (buttonConfig.background_type === 'gradient') {
      const primary = buttonConfig.primary_color || '#fb6592';
      const secondary = buttonConfig.secondary_color || '#57d5ff';
      styles.background = `linear-gradient(135deg, ${primary}, ${secondary})`;
    } else if (buttonConfig.primary_color) {
      styles.backgroundColor = buttonConfig.primary_color;
    }
    
    // Apply text color
    if (buttonConfig.text_color) {
      styles.color = buttonConfig.text_color;
    }
    
    return styles;
  };

  const CtaButton = ({ className = '' }) => (
    <button
      type="button"
      className={`skin-action-button ${ctaStyleClass} ${className} ${isButtonLoading ? 'processing' : ''}`}
      id="landing-page-desktop-button"
      onClick={handleStartAnalysis}
      disabled={isButtonLoading}
      aria-busy={isButtonLoading}
      style={getButtonStyles()}
    >
      {isButtonLoading ? (
        <>
          <span>Loading…</span>
        </>
      ) : (
        ctaButtonLabel
      )}
    </button>
  );

  return (
    <div className={`landing-page-shell page-bg${!backgroundReady ? ' landing-bg-pending' : ''}`} id="landing-page-shell">
      {isLoading && (
        <div className="landing-page-loading-overlay" id="landing-page-loading-overlay" aria-busy="true" aria-live="polite">
          <div className="landing-page-loading-content">
            <div className="landing-page-loading-spinner" aria-hidden="true" />
            <p className="landing-page-loading-text">Loading…</p>
          </div>
        </div>
      )}
      <main className="landing-page-main" id="landing-page-main">
        <div className="landing-page-container" id="landing-page-container">
          {/* Desktop View - Two Column Layout */}
          <div className={`landing-page-desktop-layout ${ctaPosition === 'bottom' ? 'cta-bottom' : 'cta-top'}`} id="landing-page-desktop-layout">
            {/* Left Side - Content */}
            <div className="landing-page-desktop-content" id="landing-page-desktop-content">
              <div className="landing-page-desktop-text" id="landing-page-desktop-text">
                <div className="landing-page-desktop-logo-wrapper" id="landing-page-desktop-logo-wrapper">
                  <img
                    src={orgLogoUrl || `${process.env.PUBLIC_URL}/logo.svg`}
                    alt="Organization logo"
                    className="landing-page-desktop-logo"
                    id="landing-page-desktop-logo"
                  />
                </div>
                <div
                  className="landing-page-desktop-description"
                  id="landing-page-desktop-description"
                  dangerouslySetInnerHTML={description ? { __html: sanitizeHtml(decodeHtmlEntities(description)) } : undefined}
                />
                {/* Button in normal position (top / inside content) */}
                <CtaButton className="cta-in-content" />
              </div>
            </div>

            {/* Right Side - Image with Analysis Overlay */}
            {imageUrl && (
              <div className="landing-page-desktop-image" id="landing-page-desktop-image">
                <img
                  src={imageUrl}
                  alt="AI Skin Analysis Visualization"
                  className={`landing-page-desktop-img ${isLoading ? 'is-loading' : ''}`}
                  id="landing-page-desktop-img"
                />
              </div>
            )}

            {/* Button below image (only visible when cta_position is 'bottom' on mobile) */}
            <CtaButton className="cta-below-image" />
          </div>
        </div>
      </main>

      <PageFooter />
    </div>
  );
};

export default LandingPage;

